import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { useMatch, useNavigate } from 'react-router-dom';
import {
  LoginMutationVariables,
  SubmitFunctionResult,
  ValidateTokenMutationResult,
  ValidateTokenMutationVariables,
} from '../types';
import {
  backgroundUrl,
  CoverPanel,
  CoverLogo,
  FormCard,
  FormPanel,
  LoginPage,
  logoUrl,
  Main,
  Shell,
  StyledContainer,
} from './login-card';
import { OidcCallbackHandler } from './oidc-login';
import { ResetPassword } from './reset-password';
import {
  RequestPasswordResetMutationResult,
  RequestPasswordResetSmsMutationResult,
  RequestPasswordResetMutationVariables,
  ResetPasswordMutationResult,
  ResetPasswordMutationVariables,
} from '..';
import { RequestPasswordReset } from './reset-password-request';
import { joinPath, toRelativePath } from './path.utils';

const createCardTheme = (primaryMain: string, secondaryMain: string) =>
  createTheme({
    spacing: 4,
    palette: {
      mode: 'light',
      primary: {
        main: primaryMain,
        contrastText: '#FFF',
      },
      secondary: {
        main: secondaryMain,
        contrastText: '#FFF',
      },
      background: { default: '#F7F9FC', paper: '#FFF' },
    },
  });

export interface LoginProps {
  loginMutation: (
    variables: LoginMutationVariables,
  ) => Promise<SubmitFunctionResult>;
  requestPasswordResetMutation: (
    variables: RequestPasswordResetMutationVariables,
  ) => Promise<RequestPasswordResetMutationResult>;
  requestPasswordResetSmsMutation?: (
    variables: RequestPasswordResetMutationVariables,
  ) => Promise<RequestPasswordResetSmsMutationResult>;
  resetPasswordMutation: (
    variables: ResetPasswordMutationVariables,
  ) => Promise<ResetPasswordMutationResult>;
  validateToken: (
    variables: ValidateTokenMutationVariables,
  ) => Promise<ValidateTokenMutationResult>;
  isLoggedIn: boolean;
  title: string;
  oidcUrl?: string | null;
  oidcCallbackUrl?: string | null;
  oidcTokenUrl?: string | null;
  oidcBtnLabel?: string | null;
  smsBtnLabel?: string | null;
  colorSchemePrimary?: string;
  colorSchemeSecondary?: string;
  postLoginPath?: string;
  pathPrefix: string;
}

// eslint-disable-next-line max-lines-per-function
export const Login: React.FC<LoginProps> = ({
  isLoggedIn,
  pathPrefix,
  oidcCallbackUrl,
  oidcTokenUrl,
  requestPasswordResetMutation,
  requestPasswordResetSmsMutation,
  resetPasswordMutation,
  smsBtnLabel,
  validateToken,
  ...loginProps
}) => {
  const navigate = useNavigate();
  const postLoginPath = loginProps.postLoginPath || '/';
  React.useEffect(() => {
    if (isLoggedIn) {
      navigate(postLoginPath, { replace: true });
    }
  }, [isLoggedIn, navigate, postLoginPath]);

  const requestPasswordResetMatch = useMatch(
    joinPath(pathPrefix, 'requestPasswordReset'),
  );
  const resetPasswordMatch = useMatch(joinPath(pathPrefix, 'resetPassword/*'));
  const oidcCallbackPath = oidcCallbackUrl
    ? joinPath(pathPrefix, toRelativePath(oidcCallbackUrl))
    : null;
  const oidcCallbackMatch = useMatch(oidcCallbackPath || '');

  let content: React.ReactNode = (
    <LoginPage {...loginProps} pathPrefix={pathPrefix} />
  );

  if (requestPasswordResetMatch) {
    content = (
      <RequestPasswordReset
        requestPasswordResetMutation={requestPasswordResetMutation}
        requestPasswordResetSmsMutation={requestPasswordResetSmsMutation}
        smsBtnLabel={smsBtnLabel}
        pathPrefix={pathPrefix}
      />
    );
  }

  if (resetPasswordMatch) {
    content = (
      <ResetPassword
        resetPasswordMutation={resetPasswordMutation}
        validateToken={validateToken}
        pathPrefix={pathPrefix}
      />
    );
  }

  if (oidcCallbackMatch && oidcCallbackUrl) {
    content = (
      <OidcCallbackHandler
        pathPrefix={pathPrefix}
        oidcTokenUrl={oidcTokenUrl}
        postLoginPath={postLoginPath}
      />
    );
  }

  return (
    <CardWrapper
      title={loginProps.title}
      hasBackground={true}
      colorSchemePrimary={loginProps.colorSchemePrimary}
      colorSchemeSecondary={loginProps.colorSchemeSecondary}
    >
      {content}
    </CardWrapper>
  );
};

export const CardWrapper: React.FC<{
  title: string;
  hasBackground?: boolean;
  colorSchemePrimary?: string;
  colorSchemeSecondary?: string;
  children?: React.ReactNode;
}> = ({
  title,
  children,
  hasBackground,
  colorSchemePrimary,
  colorSchemeSecondary,
}) => {
  const parentTheme = useTheme();
  const primaryMain = colorSchemePrimary || parentTheme.palette.primary.main;
  const secondaryMain =
    colorSchemeSecondary || parentTheme.palette.secondary.main;

  const lightTheme = useMemo(
    () => createCardTheme(primaryMain, secondaryMain),
    [primaryMain, secondaryMain],
  );

  return (
    <ThemeProvider theme={lightTheme}>
      <StyledThemeProvider theme={lightTheme}>
        <Main $url={hasBackground ? backgroundUrl : ''}>
          <Shell>
            <CoverPanel $url={backgroundUrl}>
              <CoverLogo src={logoUrl} alt="" />
            </CoverPanel>
            <FormPanel>
              <FormCard elevation={6}>
                <StyledContainer>
                  <img
                    src={logoUrl}
                    alt={`${title} Logo`}
                    height={38}
                    width={290}
                    style={{ margin: '2rem auto 1.5rem', display: 'block' }}
                  />
                  {children}
                </StyledContainer>
              </FormCard>
            </FormPanel>
          </Shell>
        </Main>
      </StyledThemeProvider>
    </ThemeProvider>
  );
};
